import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { useNavigate } from '@tanstack/react-router'
import { useEffect, useState } from 'react'
import {
  fetchGuestState,
  guestKeys,
  guestSessionQueryOptions,
  joinAsGuest,
  joinErrorInfo,
  useRoomAccessStore,
} from '@/entities/call'
import { toast } from '@/shared/ui/toast'

export type GuestJoinStatus = 'restoring' | 'form' | 'waiting'

// Гость: имя → lobby-ожидание (poll) → после допуска room-JWT в память и переход.
// Поллинг несёт meetingState — лобби честно показывает «ещё не началась» /
// «завершилась, пока вы ждали» вместо вечного спиннера.
export function useGuestJoin(token: string) {
  const navigate = useNavigate()
  const queryClient = useQueryClient()
  const setRoomAccess = useRoomAccessStore((s) => s.setRoomAccess)
  const [participantId, setParticipantId] = useState<string | null>(null)

  // Восстановление после refresh: живая cookie-сессия этой же ссылки возвращает
  // гостя в lobby (waiting — без повторного ввода имени) или сразу в комнату
  // (joined — свежий room-JWT прямо из ответа). Чужая/протухшая сессия — обычная
  // форма (mock-хендлер отвечает «сессии нет»).
  const session = useQuery({
    ...guestSessionQueryOptions(),
    enabled: participantId == null, // после явного join восстановление не нужно
  })
  const restored =
    session.data?.kind === 'active' && session.data.session.token === token
      ? session.data.session
      : null
  // Резюме waiting-гостя — производное значение (не setState в эффекте):
  // participantId из cookie-сессии подхватывает поллинг ниже как свой.
  const resumedParticipantId = restored?.state === 'waiting' ? restored.participantId : null
  const pid = participantId ?? resumedParticipantId
  useEffect(() => {
    if (restored?.state === 'joined' && restored.roomAccess) {
      setRoomAccess(restored.roomAccess, '/')
      void navigate({ to: '/room/$id', params: { id: restored.roomAccess.meetingId } })
    }
  }, [restored, navigate, setRoomAccess])

  const join = useMutation({
    mutationFn: (name: string) => joinAsGuest(token, name),
    onSuccess: (entry) => setParticipantId(entry.participantId),
    onError: (error) => {
      const info = joinErrorInfo(error)
      // Терминальный отказ (встреча завершилась/отменилась между открытием
      // страницы и кнопкой) — обновляем превью: страница покажет полноэкранный
      // статус вместо формы.
      if (info.kind === 'ended' || info.kind === 'cancelled' || info.kind === 'not-found') {
        void queryClient.invalidateQueries({ queryKey: guestKeys.meeting(token) })
      }
      toast.error(info.message)
    },
  })

  const stateQuery = useQuery({
    queryKey: ['guest-state', token, pid],
    queryFn: () => {
      if (pid == null) throw new Error('participantId ещё не получен')
      return fetchGuestState(token, pid)
    },
    enabled: pid != null,
    // Поллинг не останавливается на сетевой ошибке (сбой самовосстанавливается);
    // стоп — когда получен room-JWT или встреча пришла к терминальному состоянию.
    refetchInterval: (query) => {
      const data = query.state.data
      if (data?.roomAccess) return false
      if (data?.meetingState === 'ended' || data?.meetingState === 'cancelled') return false
      return 2000
    },
  })

  const access = stateQuery.data?.roomAccess
  useEffect(() => {
    if (!access) return
    setRoomAccess(access, '/')
    void navigate({ to: '/room/$id', params: { id: access.meetingId } })
  }, [access, navigate, setRoomAccess])

  // isLoading (а не isPending): после явного join query отключается и навсегда
  // остаётся pending; loading = реально идёт fetch восстановления.
  const status: GuestJoinStatus = pid ? 'waiting' : session.isLoading ? 'restoring' : 'form'
  return {
    status,
    meetingState: stateQuery.data?.meetingState,
    participantState: stateQuery.data?.state,
    pollFailed: stateQuery.isError,
    submit: (name: string) => join.mutate(name),
    isSubmitting: join.isPending,
  }
}
